import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ShoppingBag, Home } from "lucide-react";
import { Button } from "../components/ui/button";
import api from "../lib/api";

export default function ShopSuccess() {
  const [params] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/products/order-status/${sessionId}`);
        if (data.order?.status === "paid" || data.payment_status === "paid") {
          setOrder(data.order);
          setStatus("paid");
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        // Poll up to 20 times every 2.5s (50s total) — webhook usually lands in <10s
        if (attempts < 20) setTimeout(poll, 2500);
        else setStatus("pending");
      } catch {
        if (attempts < 20) setTimeout(poll, 2500);
        else setStatus("error");
      }
    };
    poll();
  }, [params]);

  return (
    <div className="max-w-3xl mx-auto px-6 py-24 lg:py-32" data-testid="shop-success-page">
      {status === "loading" && (
        <div className="text-center text-muted-foreground">Vérification de votre paiement…</div>
      )}
      {status === "paid" && order && (
        <div className="rounded-2xl border border-primary/30 bg-card p-10 lg:p-14 text-center">
          <CheckCircle2 className="w-14 h-14 text-primary mx-auto mb-6" strokeWidth={1.5} />
          <h1 className="font-display text-4xl lg:text-5xl mb-4">Commande confirmée !</h1>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Merci {order.customer_name}. Un email de confirmation a été envoyé à <b>{order.customer_email}</b>.
            {order.delivery_mode === "shipping"
              ? " Votre colis sera expédié sous 2-3 jours ouvrés."
              : " Vous pouvez venir retirer vos bouteilles au bar aux horaires d'ouverture (Mar-Sam 17h-23h)."}
          </p>
          <div className="text-left max-w-md mx-auto border-t border-border pt-6 mb-8">
            {order.lines.map((l, i) => (
              <div key={`${l.name}-${i}`} className="flex justify-between text-sm py-1">
                <span>{l.quantity} × {l.name}</span>
                <span className="gold-text">{l.line_total.toFixed(2)} €</span>
              </div>
            ))}
            <div className="flex justify-between font-display text-xl pt-3 border-t border-border mt-3">
              <span>Total</span>
              <span className="gold-text">{order.total.toFixed(2)} €</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <Button asChild className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/boutique" data-testid="shop-success-back-btn"><ShoppingBag className="w-4 h-4 mr-2" /> Retour à la boutique</Link>
            </Button>
            <Button asChild variant="outline" className="rounded-full">
              <Link to="/"><Home className="w-4 h-4 mr-2" /> Accueil</Link>
            </Button>
          </div>
        </div>
      )}
      {status === "pending" && (
        <div className="text-center">
          <h1 className="font-display text-3xl mb-3">Paiement en cours de traitement</h1>
          <p className="text-muted-foreground mb-6">Merci, votre paiement est en cours de validation. Vous recevrez un email dès qu'il sera confirmé.</p>
          <Button asChild><Link to="/">Retour à l'accueil</Link></Button>
        </div>
      )}
      {(status === "expired" || status === "error") && (
        <div className="text-center">
          <h1 className="font-display text-3xl mb-3">Une erreur est survenue</h1>
          <p className="text-muted-foreground mb-6">Le paiement n'a pas été finalisé. Veuillez réessayer.</p>
          <Button asChild><Link to="/boutique">Retour à la boutique</Link></Button>
        </div>
      )}
    </div>
  );
}
